import { validateURL } from './helpers';

// Not every error body is JSON. The cardholder-portal internal pipeline answers
// an expired or missing token with a bare 401 string ("Missing Authorization
// Header"), and res.json() on that throws a SyntaxError about an unexpected
// token -- which then reaches the cardholder as if it were an API message.
// Parse defensively instead, and carry the status so callers can tell an
// authentication failure from a rejected request.
const parseBody = async res => {
  // No-content responses (204) have an empty body; res.json() would throw
  if (res.status === 204) return {};

  const text = await res.text();
  const failed = message => {
    const error = new Error(message);
    error.status = res.status;
    return error;
  };

  // An empty body says nothing, so the status has to: a failure with no message
  // must not read as success.
  if (!text) {
    if (res.ok) return {};
    throw failed(`Request failed with status ${res.status}`);
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    // A successful response that is not JSON is a genuine surprise; let it
    // raise. A failed one is just an error reported as plain text.
    if (res.ok) throw e;
    throw failed(text);
  }
};

// Anything thrown from a response carries its status, so a caller can react to
// a 401 without parsing the message.
const withStatus = (error, res) =>
  Object.assign(error, { status: res.status });

export const get = async url => {
  const updatedUrl = validateURL(url) || url;

  const response = await fetch(updatedUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    credentials: 'include',
  })
    .then(async res => ({ res, body: await parseBody(res) }))
    .then(({ res, body }) => {
      if (body.error) {
        if (body.error.message) throw withStatus(new Error(body.error.message), res);
        throw withStatus(new Error('Failed to fetch data.'), res);
      }
      return body;
    });

  return response;
};

export const post = async (url, body) => {
  const updatedUrl = validateURL(url) || url;

  const response = await fetch(updatedUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  })
    .then(async res => ({ res, body: await parseBody(res) }))
    .then(({ res, body }) => {
      if (body.error) {
        if (body.error.message) {
          /* eslint-disable-next-line camelcase */
          const { field_errors, message, code } = body.error;

          let errorMessage = message;

          /* eslint-disable-next-line camelcase */
          if (field_errors && field_errors.length > 0) {
            /* eslint-disable-next-line camelcase */
            const fieldErrorMessages = field_errors
              .map(fieldError => `${fieldError.position.join(', ')}`)
              .join(' | ');

            errorMessage = `${errorMessage}: ${fieldErrorMessages}`;
          }
          if (url.includes('register')) {
            const parsedError = {
              message: errorMessage,
              /* eslint-disable-next-line camelcase */
              fieldErrors: field_errors,
              code,
              status: res.status,
            };
            throw parsedError; // throw a structured object
          } else {
            const error = new Error(errorMessage);
            error.code = code;
            throw withStatus(error, res);
          }
        }
        const error = new Error('Failed to send request.');
        error.code = body.error.code;
        throw withStatus(error, res);
      }
      return body;
    });

  return response;
};

export const put = async (url, body) => {
  const updatedUrl = validateURL(url) || url;

  const response = await fetch(updatedUrl, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  })
    .then(async res => ({ res, body: await parseBody(res) }))
    .then(({ res, body: data }) => {
      if (data.error) {
        if (data.error.message) throw withStatus(new Error(data.error.message), res);
        throw withStatus(new Error('Failed to update data.'), res);
      }
      return data;
    });

  return response;
};

export const deleteHTTP = async (url, body) => {
  const updatedUrl = validateURL(url) || url;

  const response = await fetch(updatedUrl, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  })
    .then(async res => ({ res, body: await parseBody(res) }))
    .then(({ res, body }) => {
      if (body.error) {
        if (body.error.message) throw withStatus(new Error(body.error.message), res);
        throw withStatus(new Error('Failed to send request.'), res);
      }
      return body;
    });

  return response;
};
